import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useClients } from '../hooks/useClients';
import { useMessages } from '../hooks/useMessages';
import { useStats } from '../hooks/useStats';
import { useSponsors } from '../hooks/useSponsors';
import { useVouchers } from '../hooks/useVouchers';
import { useDebounce } from '../hooks/useDebounce';
import { useTenantConfig } from '../hooks/useTenantConfig';
import api from '../../../services/api';

const ProContext = createContext();

export const useProContext = () => {
  const ctx = useContext(ProContext);
  if (!ctx) throw new Error('useProContext must be used within a ProProvider');
  return ctx;
};

export const ProProvider = ({ children }) => {
  const { currentUser, logout, updateUser } = useAuth();

  // Branding
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem('branding') || 'null');
      if (s) setBranding(s);
    } catch {}
  }, [currentUser]);
  const brand = branding?.primaryColor || '#0068FF';
  const brandLogo = branding?.logoUrl || currentUser?.logo || null;
  const brandName = branding?.name || currentUser?.optician_name || '';

  const isAA = (currentUser?.user_type || 'parrainage') === 'apport_affaires';

  // Month selector
  const [month, setMonth] = useState(new Date());
  const dm = useDebounce(month, 300);

  // Hooks
  const { stats, loading: statsLoading, loadStats } = useStats();
  const {
    clients, customFields, loading: clientsLoading,
    fetchClients, updateClientStatus,
  } = useClients(currentUser);
  const { messages, unreadMessages, fetchMessages, sendMessage, markAsRead } = useMessages(currentUser?.id);
  const { sponsors, monthlyTotal, fetchSponsors } = useSponsors();
  const { vouchers, fetchVouchers } = useVouchers();
  const { isOptionActive, activeOptions, optionConfigs } = useTenantConfig(currentUser?.id);

  // Admin option data
  const [rdvList, setRdvList] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);

  // Get brand slug from branding localStorage or fallback
  const brandSlug = useMemo(() => {
    try {
      const b = JSON.parse(localStorage.getItem('branding') || 'null');
      return b?.slug || '';
    } catch { return ''; }
  }, [currentUser]);

  const tenantHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'x-tenant-slug': brandSlug,
    }),
    [brandSlug]
  );

  const fetchAllOptionData = useCallback(async () => {
    const h = tenantHeaders();
    const fetches = [];
    if (isOptionActive('appointment'))
      fetches.push(
        api.get('/appointments/admin', { headers: h })
          .then(r => {
            const raw = Array.isArray(r.data) ? r.data : r.data?.data || [];
            // L'agenda pro n'affiche pas les RDV annulés.
            setRdvList(raw.filter(rdv => rdv.status !== 'cancelled'));
          })
          .catch(() => {})
      );
    if (isOptionActive('google-review'))
      fetches.push(
        api.get('/google-reviews/stats', { headers: h })
          .then(r => setReviewStats(r.data))
          .catch(() => {})
      );
    await Promise.all(fetches);
  }, [tenantHeaders, isOptionActive]);

  // Initial data load
  // eslint-disable-next-line
  useEffect(() => {
    if (!currentUser) return;
    Promise.all([fetchClients(), fetchSponsors(), fetchVouchers(), loadStats(dm)])
      .then(() => fetchMessages(false, clients));
  }, [currentUser]);

  // Fetch option data when activeOptions are loaded
  // eslint-disable-next-line
  useEffect(() => {
    if (!currentUser || activeOptions.length === 0) return;
    fetchAllOptionData();
  }, [currentUser, activeOptions.join(',')]);

  // Reload stats on month change
  // eslint-disable-next-line
  useEffect(() => {
    if (currentUser && dm) loadStats(dm);
  }, [dm]);

  // Computed
  const ongoing = clients.filter(c => ['pending', 'refund', 'payed'].includes(c.status));
  const completed = clients.filter(c => ['approved', 'rejected', 'delivered'].includes(c.status));

  const value = {
    // Auth
    currentUser,
    logout,
    updateUser,
    // Branding
    brand,
    brandLogo,
    brandName,
    branding,
    isAA,
    // Month
    month,
    setMonth,
    dm,
    // Clients
    clients,
    customFields,
    clientsLoading,
    ongoing,
    completed,
    fetchClients,
    updateClientStatus,
    // Messages
    messages,
    unreadMessages,
    fetchMessages,
    sendMessage,
    markAsRead,
    // Stats
    stats,
    statsLoading,
    loadStats,
    // Sponsors
    sponsors,
    monthlyTotal,
    fetchSponsors,
    // Vouchers
    vouchers,
    fetchVouchers,
    // Tenant config
    isOptionActive,
    activeOptions,
    optionConfigs,
    // Option data
    rdvList,
    setRdvList,
    reviewStats,
    fetchAllOptionData,
    // Helpers
    tenantHeaders,
  };

  return <ProContext.Provider value={value}>{children}</ProContext.Provider>;
};

export default ProContext;
